import type {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexGrid,
  ApexLegend,
  ApexMarkers,
  ApexStroke,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis,
} from 'ng-apexcharts';
import type { MonthlyReportDailyPoint } from '../monthly-report.models';
import {
  formatLimaShortDate,
  roundOrNull,
} from '../../institution-history/utils/traffic-format.util';

export type MonthlyReportChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis | ApexYAxis[];
  stroke: ApexStroke;
  dataLabels: ApexDataLabels;
  colors: string[];
  legend: ApexLegend;
  tooltip: ApexTooltip;
  grid: ApexGrid;
  markers: ApexMarkers;
};

const PARTIAL_MARKER = '#f59e0b';

export function hasDailyTrafficPoints(series: MonthlyReportDailyPoint[] | null | undefined): boolean {
  if (!series?.length) return false;
  return series.some((point) => point.downloadMaxMbps != null || point.uploadMaxMbps != null);
}

/**
 * Gráfico diario del reporte. Mbps ya vienen del backend; los huecos se
 * representan con `null` (Apex 3.37 no declara `connectNulls`).
 */
export function buildMonthlyReportDailyChart(
  series: MonthlyReportDailyPoint[],
): MonthlyReportChartOptions {
  const categories = series.map((point) => formatLimaShortDate(point.date));
  const download = series.map((point) => roundOrNull(point.downloadMaxMbps, 2));
  const upload = series.map((point) => roundOrNull(point.uploadMaxMbps, 2));

  const discrete: NonNullable<ApexMarkers['discrete']> = [];
  series.forEach((point, index) => {
    if (point.dayStatus !== 'PARTIAL') return;
    discrete.push(
      {
        seriesIndex: 0,
        dataPointIndex: index,
        fillColor: PARTIAL_MARKER,
        strokeColor: PARTIAL_MARKER,
        size: 5,
      },
      {
        seriesIndex: 1,
        dataPointIndex: index,
        fillColor: PARTIAL_MARKER,
        strokeColor: PARTIAL_MARKER,
        size: 5,
      },
    );
  });

  return {
    series: [
      { name: 'Bajada', data: download as number[] },
      { name: 'Subida', data: upload as number[] },
    ],
    chart: { type: 'line', height: 300, toolbar: { show: false }, animations: { enabled: false } },
    colors: ['#0d9488', '#0284c7'],
    stroke: { curve: 'straight', width: 2 },
    dataLabels: { enabled: false },
    markers: {
      size: 3,
      showNullDataPoints: false,
      discrete,
    },
    xaxis: { categories, tickAmount: Math.min(10, categories.length) },
    yaxis: { title: { text: 'Mbps' }, min: 0 },
    legend: { position: 'top' },
    tooltip: {
      y: {
        formatter: (value: number) =>
          value == null || Number.isNaN(value) ? '—' : `${Number(value).toFixed(2)} Mbps`,
      },
    },
    grid: { strokeDashArray: 3 },
  };
}

/**
 * Variante específica de gráfico para PDF (alta resolución y contraste de impresión).
 * - Líneas de series más gruesas (width 3).
 * - Fondo blanco sólido (#ffffff).
 * - Altura útil ~360 px (≈180 pt a ancho A4) para caber en una página sin aplastar curvas.
 * - Labels y ejes con tipografía de alto contraste (#334155 / #0f172a, peso 600).
 * - Leyenda con mayor presencia y legibilidad.
 * - Grid sutil pero nítido (#e2e8f0 con líneas en ambos ejes).
 */
export function buildMonthlyReportPdfChart(
  series: MonthlyReportDailyPoint[],
): MonthlyReportChartOptions {
  const categories = series.map((point) => formatLimaShortDate(point.date));
  const download = series.map((point) => roundOrNull(point.downloadMaxMbps, 2));
  const upload = series.map((point) => roundOrNull(point.uploadMaxMbps, 2));

  const discrete: NonNullable<ApexMarkers['discrete']> = [];
  series.forEach((point, index) => {
    if (point.dayStatus !== 'PARTIAL') return;
    discrete.push(
      {
        seriesIndex: 0,
        dataPointIndex: index,
        fillColor: PARTIAL_MARKER,
        strokeColor: PARTIAL_MARKER,
        size: 5.5,
      },
      {
        seriesIndex: 1,
        dataPointIndex: index,
        fillColor: PARTIAL_MARKER,
        strokeColor: PARTIAL_MARKER,
        size: 5.5,
      },
    );
  });

  return {
    series: [
      { name: 'Bajada', data: download as number[] },
      { name: 'Subida', data: upload as number[] },
    ],
    chart: {
      type: 'line',
      height: 360,
      background: '#ffffff',
      toolbar: { show: false },
      animations: { enabled: false },
    },
    colors: ['#0d9488', '#0284c7'],
    stroke: { curve: 'straight', width: 3 },
    dataLabels: { enabled: false },
    markers: {
      size: 3.5,
      showNullDataPoints: false,
      discrete,
    },
    xaxis: {
      categories,
      tickAmount: Math.min(12, categories.length),
      axisBorder: { show: true, color: '#cbd5e1' },
      axisTicks: { show: true, color: '#cbd5e1' },
      labels: {
        style: {
          colors: '#334155',
          fontSize: '11px',
          fontWeight: 600,
          fontFamily: 'Helvetica, Arial, sans-serif',
        },
        offsetY: 2,
      },
    },
    yaxis: {
      title: {
        text: 'Mbps',
        style: {
          color: '#0f172a',
          fontSize: '11px',
          fontWeight: 700,
          fontFamily: 'Helvetica, Arial, sans-serif',
        },
      },
      min: 0,
      labels: {
        style: {
          colors: '#334155',
          fontSize: '11px',
          fontWeight: 600,
          fontFamily: 'Helvetica, Arial, sans-serif',
        },
      },
    },
    legend: {
      show: true,
      position: 'top',
      horizontalAlign: 'center',
      fontSize: '12px',
      fontFamily: 'Helvetica, Arial, sans-serif',
      fontWeight: 600,
      labels: {
        colors: '#0f172a',
      },
      markers: {
        width: 12,
        height: 12,
        radius: 3,
      },
      itemMargin: {
        horizontal: 16,
        vertical: 4,
      },
    },
    tooltip: { enabled: false },
    grid: {
      borderColor: '#e2e8f0',
      strokeDashArray: 2,
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: true } },
      padding: {
        top: 0,
        right: 16,
        bottom: 0,
        left: 10,
      },
    },
  };
}
